/**
 * Prefill Shopify checkout with the pickup store selected in the theme.
 *
 * The Online Store Ajax cart cannot set buyer delivery preferences, so the
 * normal checkout submit is bridged to a Storefront API cart. If the bridge
 * fails, the original checkout submit continues unchanged.
 */
(function initPickupCheckout(global) {
  const phoneValidation = global.BFFPhoneValidation
    || (typeof require === 'function' ? require('./phone-validation.js') : null);
  const DEFAULT_API_VERSION = '2026-07';
  const CONFIG_ID = 'bff-storefront-cart-config';
  const CHECKOUT_MUTATION = `
    mutation CreatePickupCheckout($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          buyerIdentity {
            preferences {
              delivery {
                deliveryMethod
                pickupHandle
              }
            }
          }
        }
        userErrors {
          field
          message
          code
        }
        warnings {
          code
          message
        }
      }
    }
  `;

  function toGid(type, id) {
    if ( id === null || id === undefined || id === '' ) return null;
    const value = String(id);
    return value.startsWith('gid://shopify/') ? value : `gid://shopify/${type}/${value}`;
  }

  function toAttributes(values) {
    if ( ! values || typeof values !== 'object' ) return [];

    return Object.entries(values)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key: String(key),
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }));
  }

  function cartLineFromAjaxItem(item) {
    const line = {
      merchandiseId: toGid('ProductVariant', item.variant_id),
      quantity: Number(item.quantity) || 1,
    };
    const attributes = toAttributes(item.properties);
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;

    if ( attributes.length ) line.attributes = attributes;
    if ( sellingPlanId ) line.sellingPlanId = toGid('SellingPlan', sellingPlanId);

    return line;
  }

  function cartInputFromAjaxCart(cart, store) {
    const input = {
      lines: (cart.items || []).map(cartLineFromAjaxItem),
      buyerIdentity: {
        preferences: {
          delivery: {
            deliveryMethod: ['PICK_UP'],
            pickupHandle: [String(store.location_id)],
          },
        },
      },
    };
    const attributes = toAttributes(cart.attributes);
    const discountCodes = (cart.discount_codes || [])
      .filter(discount => discount?.code && discount.applicable !== false)
      .map(discount => discount.code);

    if ( attributes.length ) input.attributes = attributes;
    if ( cart.note ) input.note = cart.note;
    if ( discountCodes.length ) input.discountCodes = discountCodes;

    return input;
  }

  function mergeCurrentFormState(cart, entries) {
    const attributes = { ...(cart.attributes || {}) };
    let note = cart.note || '';

    for ( const [key, value] of entries ) {
      const attributeMatch = String(key).match(/^attributes\[(.+)]$/);

      if ( attributeMatch ) {
        attributes[attributeMatch[1]] = String(value);
      } else if ( key === 'note' ) {
        note = String(value);
      }
    }

    return { ...cart, attributes, note };
  }

  function mergePickupSchedulerState(cart, scheduler) {
    if ( ! scheduler?.querySelector ) return cart;

    const attributes = { ...(cart.attributes || {}) };
    const fields = [
      ['Pickup date', '[data-pickup-date]'],
      ['Pickup time', '[data-pickup-time]'],
      ['Preferred store', '[data-preferred-store]'],
      ['Customer Phone Number', '[data-customer-phone]'],
    ];

    fields.forEach(([key, selector]) => {
      const control = scheduler.querySelector(selector);

      if ( control ) attributes[key] = String(control.value || '');
    });

    return { ...cart, attributes };
  }

  function checkoutTimeFromResponse(response, fallback = new Date()) {
    const responseDate = response?.headers?.get?.('date');

    if ( ! responseDate ) return fallback;

    const parsed = new Date(responseDate);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
  }

  function validatePickupDate(cart, store, now, utils = global.StoreHoursUtils) {
    const selectedDate = String(cart?.attributes?.['Pickup date'] || '');

    if ( ! selectedDate || ! store || ! utils?.firstAvailablePickupDate ) {
      return { valid: true, selectedDate, earliestDate: '' };
    }

    const earliestDate = utils.toISO(utils.firstAvailablePickupDate(store, now));

    return {
      valid: selectedDate >= earliestDate,
      selectedDate,
      earliestDate,
      cutoffPassed: ! utils.isOrderBeforePickupCutoff(store, now),
    };
  }

  function validatePickupDetails(cart, store, now, utils = global.StoreHoursUtils) {
    const selectedDate = String(cart?.attributes?.['Pickup date'] || '');
    const selectedTime = String(cart?.attributes?.['Pickup time'] || '');
    const customerPhone = String(cart?.attributes?.['Customer Phone Number'] || '').trim();

    if ( ! store?.location_id ) {
      return { valid: false, reason: 'missing-store', selectedDate, selectedTime, customerPhone };
    }

    if ( ! customerPhone ) {
      return { valid: false, reason: 'missing-phone', selectedDate, selectedTime, customerPhone };
    }

    if ( ! phoneValidation?.isValidAustralianPhoneNumber(customerPhone) ) {
      return { valid: false, reason: 'invalid-phone', selectedDate, selectedTime, customerPhone };
    }

    if ( ! selectedDate ) {
      return { valid: false, reason: 'missing-date', selectedDate, selectedTime };
    }

    const dateValidation = validatePickupDate(cart, store, now, utils);
    if ( ! dateValidation.valid ) {
      return { ...dateValidation, valid: false, reason: 'date-too-early', selectedTime };
    }

    if ( utils?.isPickupClosedOnDate?.(store, utils.parseISODate?.(selectedDate)) ) {
      return { ...dateValidation, valid: false, reason: 'unavailable-date', selectedTime };
    }

    const configuredWindows = store.time_windows || [];
    const availableWindows = utils?.filterTimeWindows
      ? utils.filterTimeWindows(store, selectedDate, configuredWindows, now)
      : configuredWindows;

    if ( configuredWindows.length && ! availableWindows.length ) {
      return { ...dateValidation, valid: false, reason: 'unavailable-time', selectedTime, availableWindows };
    }

    if ( availableWindows.length && ! selectedTime ) {
      return { ...dateValidation, valid: false, reason: 'missing-time', selectedTime, availableWindows };
    }

    if ( availableWindows.length && selectedTime && ! availableWindows.includes(selectedTime) ) {
      return { ...dateValidation, valid: false, reason: 'invalid-time', selectedTime, availableWindows };
    }

    return { ...dateValidation, valid: true, reason: '', selectedTime, availableWindows };
  }

  function pickupSchedulerForSubmitter(submitter) {
    const drawer = submitter?.closest?.('#site-cart-sidebar');

    if ( drawer ) {
      return drawer.querySelector('pickup-scheduler');
    }

    return global.document?.querySelector('pickup-scheduler[data-context="page"]')
      || global.document?.querySelector('pickup-scheduler');
  }

  function showPickupValidationError(form, submitter, store, validation, now) {
    const scheduler = pickupSchedulerForSubmitter(submitter);
    let message = 'Please confirm your pickup details before checkout.';

    if ( validation.reason === 'date-too-early' ) {
      const formattedDate = global.StoreHoursUtils?.formatPickupDate(
        validation.earliestDate,
        store.timezone,
      ) || validation.earliestDate;
      message = validation.cutoffPassed
        ? `The 3pm cutoff has passed. The earliest available pickup is now ${formattedDate}. Please confirm your pickup details.`
        : `Your pickup date is no longer available. The earliest available pickup is now ${formattedDate}. Please confirm your pickup details.`;

      global.document?.querySelectorAll('pickup-scheduler').forEach(instance => {
        instance.applyStoreConfig?.(false, now);
      });
    } else if ( validation.reason === 'missing-store' ) {
      message = 'Please select a pickup store before checkout.';
    } else if ( validation.reason === 'missing-phone' ) {
      message = 'Please enter a customer phone number before checkout.';
    } else if ( validation.reason === 'invalid-phone' ) {
      message = 'Please enter a valid Australian mobile or landline number.';
    } else if ( ['missing-date', 'unavailable-date'].includes(validation.reason) ) {
      message = 'Please select an available pickup date before checkout.';
    } else if ( validation.reason === 'missing-time' ) {
      message = 'Please select a pickup time before checkout.';
    } else if ( ['unavailable-time', 'invalid-time'].includes(validation.reason) ) {
      message = 'That pickup time is no longer available. Please select another time.';
    } else if ( validation.reason === 'checkout-error' ) {
      message = 'We could not start checkout. Please try again.';
    }

    scheduler?.setStatus?.(message);
    global.cartDrawerPickup?.syncAction?.();
    submitter.removeAttribute('aria-busy');
    form.classList.remove('processing');
  }

  function showPickupDateChanged(form, submitter, store, validation, now) {
    showPickupValidationError(form, submitter, store, {
      ...validation,
      reason: 'date-too-early',
    }, now);
  }

  function loadConfig(documentNode = global.document) {
    const node = documentNode?.getElementById(CONFIG_ID);
    if ( ! node ) return {};

    try {
      return JSON.parse(node.textContent) || {};
    } catch ( error ) {
      return {};
    }
  }

  function storefrontEndpoint(config) {
    const version = /^\d{4}-\d{2}$/.test(config.api_version || '')
      ? config.api_version
      : DEFAULT_API_VERSION;
    return `${global.location.origin}/api/${version}/graphql.json`;
  }

  async function createPickupCheckout(cart, store, config) {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    const response = await global.fetch(storefrontEndpoint(config), {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify({
        query: CHECKOUT_MUTATION,
        variables: { input: cartInputFromAjaxCart(cart, store) },
      }),
    });
    const payload = await response.json();
    const result = payload.data?.cartCreate;

    if ( ! response.ok || payload.errors?.length || result?.userErrors?.length || ! result?.cart?.checkoutUrl ) {
      const message = result?.userErrors?.map(error => error.message).join(', ')
        || payload.errors?.map(error => error.message).join(', ')
        || `Storefront cart request failed (${response.status})`;
      throw new Error(message);
    }

    return result.cart.checkoutUrl;
  }

  function isCheckoutSubmit(event) {
    const submitter = event.submitter || global.document?.activeElement;
    return submitter?.name === 'checkout' ? submitter : null;
  }

  function resumeNativeCheckout(form, submitter) {
    form.dataset.bffPickupCheckoutBypass = 'true';
    submitter?.removeAttribute('aria-busy');
    form.classList.remove('processing');

    if ( typeof form.requestSubmit === 'function' ) {
      form.requestSubmit(submitter);
    } else {
      const input = global.document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = 'Checkout';
      form.appendChild(input);
      form.submit();
    }
  }

  async function onSubmit(event) {
    const form = event.target;
    const submitter = isCheckoutSubmit(event);

    if ( ! submitter || !(form instanceof global.HTMLFormElement) ) return;

    if ( form.dataset.bffPickupCheckoutBypass === 'true' ) {
      delete form.dataset.bffPickupCheckoutBypass;
      return;
    }

    const handle = global.localStorage?.getItem('selected-store');
    const store = global.StoreHoursUtils?.getPickupStore(handle);
    const pickupRequired = submitter.hasAttribute('data-pickup-checkout');

    if ( ! store?.location_id && ! pickupRequired ) return;

    event.preventDefault();
    submitter.setAttribute('aria-busy', 'true');
    form.classList.add('processing');

    if ( ! store?.location_id ) {
      showPickupValidationError(form, submitter, store, { reason: 'missing-store' });
      return;
    }

    try {
      const root = global.Shopify?.routes?.root || '/';
      const cartResponse = await global.fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if ( ! cartResponse.ok ) {
        throw new Error(`Ajax cart request failed (${cartResponse.status})`);
      }

      const cart = await cartResponse.json();
      const formCart = mergeCurrentFormState(cart, new global.FormData(form).entries());
      const currentCart = mergePickupSchedulerState(formCart, pickupSchedulerForSubmitter(submitter));
      const checkoutTime = checkoutTimeFromResponse(cartResponse);
      const pickupDetails = validatePickupDetails(currentCart, store, checkoutTime);

      if ( ! pickupDetails.valid && (pickupRequired || ['missing-phone', 'invalid-phone'].includes(pickupDetails.reason)) ) {
        showPickupValidationError(form, submitter, store, pickupDetails, checkoutTime);
        return;
      }

      const checkoutUrl = await createPickupCheckout(currentCart, store, loadConfig());
      global.location.assign(checkoutUrl);
    } catch ( error ) {
      console.error('[BFF pickup checkout]', error);
      if ( pickupRequired ) {
        showPickupValidationError(form, submitter, store, { reason: 'checkout-error' });
      } else {
        resumeNativeCheckout(form, submitter);
      }
    }
  }

  function install() {
    global.document?.addEventListener('submit', onSubmit, true);
  }

  const helpers = {
    CHECKOUT_MUTATION,
    cartInputFromAjaxCart,
    cartLineFromAjaxItem,
    checkoutTimeFromResponse,
    loadConfig,
    mergeCurrentFormState,
    mergePickupSchedulerState,
    pickupSchedulerForSubmitter,
    showPickupValidationError,
    storefrontEndpoint,
    showPickupDateChanged,
    toAttributes,
    toGid,
    validatePickupDate,
    validatePickupDetails,
  };

  global.BFFPickupCheckout = helpers;

  if ( typeof module !== 'undefined' && module.exports ) {
    module.exports = helpers;
  }

  if ( global.document ) install();
})(typeof window !== 'undefined' ? window : globalThis);
